import { forwardRef, ReactNode } from "react";

export type Props = {
  /** Button children */
  children?: ReactNode;
  /** Disabled state */
  disabled?: boolean;
  /** Loading state - disables button and shows aria-busy */
  isLoading?: boolean;
  /** Button type */
  type?: "button" | "submit" | "reset";
  /** Click handler */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Additional CSS classes */
  className?: string;
  /** Accessibility label */
  "aria-label"?: string;
  /** ID of the element that describes this button */
  "aria-describedby"?: string;
  /** Whether the button is pressed (for toggle buttons) */
  "aria-pressed"?: boolean;
  /** Whether the button controls an expanded element */
  "aria-expanded"?: boolean;
  /** ID of the element this button controls */
  "aria-controls"?: string;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base button component with no styling.
 * Use this as the foundation for styled button components.
 *
 * @example
 * // In shared/ui/Button.tsx
 * import { ButtonBase, type Props as ButtonBaseProps } from "@core/ui";
 *
 * export function Button({ className, variant, ...props }: ButtonBaseProps & { variant?: string }) {
 *   return (
 *     <ButtonBase
 *       className={twMerge("base-styles", variant === "primary" && "primary-styles", className)}
 *       {...props}
 *     />
 *   );
 * }
 */
export const ButtonBase = forwardRef<HTMLButtonElement, Props>(
  (
    {
      children,
      disabled,
      isLoading,
      type = "button",
      onClick,
      className,
      "data-testid": testId,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        onClick={onClick}
        className={className}
        data-testid={testId ?? "button"}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {children}
      </button>
    );
  }
);

ButtonBase.displayName = "ButtonBase";

